import React, { useState } from "react";
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  HStack,
  Spinner,
  Center,
  Text,
  useToast,
  Heading,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { 
  useAdminTransactions, 
  Transaction, 
  useAdminPayouts, 
  Payout, 
  useApprovePayout, 
  useRejectPayout, 
  useMarkPayoutPaid 
} from "api/admin";

export default function Transactions(): JSX.Element {
  const [tPage, setTPage] = useState(1);
  const [pPage, setPPage] = useState(1);
  
  const { data: tData, isLoading: tLoading, isError: tError } = useAdminTransactions({ page: tPage, limit: 10 });
  const { data: pData, isLoading: pLoading, isError: pError } = useAdminPayouts({ page: pPage, limit: 10 });
  
  const approveMut = useApprovePayout();
  const rejectMut = useRejectPayout();
  const markPaidMut = useMarkPayoutPaid();
  const toast = useToast();

  const handlePayout = async (action: any, id: string, msg: string) => {
    if (!window.confirm(`Are you sure you want to ${msg} this payout?`)) return;
    try {
      await action.mutateAsync(id);
      toast({ title: `Payout ${msg} successfully`, status: "success", isClosable: true });
    } catch (e) {
      toast({ title: `Failed to ${msg}`, status: "error", isClosable: true });
    }
  };

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="20px" mb="20px">
        <Heading size="md" mb="4">Payouts</Heading>
        {pLoading ? (
        <Center p="40px"><Spinner /></Center>
      ) : pError ? (
        <Center p="40px"><Text color="red.500">Failed to load data.</Text></Center>
      ) : !pData?.data?.length ? (
        <Center p="40px"><Text>No data found.</Text></Center>
      ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Vendor</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pData?.data?.map((p: Payout) => (
                  <Tr key={p.id}>
                    <Td>{p.id.substring(0, 8)}</Td>
                    <Td>{p.vendor?.fullName}</Td>
                    <Td>₦{(p.amount ?? 0).toLocaleString()}</Td>
                    <Td><Badge>{p.status}</Badge></Td>
                    <Td>
                      <HStack spacing={2}>
                        {p.status === 'pending' && (
                          <>
                            <Button size="sm" colorScheme="green" onClick={() => handlePayout(approveMut, p.id, 'approve')}>Approve</Button>
                            <Button size="sm" colorScheme="red" onClick={() => handlePayout(rejectMut, p.id, 'reject')}>Reject</Button>
                          </>
                        )}
                        {p.status === 'approved' && (
                          <Button size="sm" colorScheme="blue" onClick={() => handlePayout(markPaidMut, p.id, 'mark paid')}>Mark Paid</Button>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Flex justify="space-between" mt="4">
              <Button disabled={pPage === 1} onClick={() => setPPage(p => p - 1)}>Prev</Button>
              <Text>Page {pData?.page} of {(pData?.totalPage || 0)}</Text>
              <Button disabled={pPage >= (pData?.totalPage || 0)} onClick={() => setPPage(p => p + 1)}>Next</Button>
            </Flex>
          </Box>
        )}
      </Card>

      <Card p="20px">
        <Heading size="md" mb="4">Transactions</Heading>
        {tLoading ? (
        <Center p="40px"><Spinner /></Center>
      ) : tError ? (
        <Center p="40px"><Text color="red.500">Failed to load data.</Text></Center>
      ) : !tData?.data?.length ? (
        <Center p="40px"><Text>No data found.</Text></Center>
      ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>User</Th>
                  <Th>Amount</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tData?.data?.map((t: Transaction) => (
                  <Tr key={t.id}>
                    <Td>{t.id.substring(0, 8)}</Td>
                    <Td>{t.user?.fullName}</Td>
                    <Td>₦{(t.amount ?? 0).toLocaleString()}</Td>
                    <Td><Badge>{t.type} / {t.tag}</Badge></Td>
                    <Td><Badge colorScheme={t.status === 'success' ? 'green' : 'yellow'}>{t.status}</Badge></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Flex justify="space-between" mt="4">
              <Button disabled={tPage === 1} onClick={() => setTPage(p => p - 1)}>Prev</Button>
              <Text>Page {tData?.page} of {(tData?.totalPage || 0)}</Text>
              <Button disabled={tPage >= (tData?.totalPage || 0)} onClick={() => setTPage(p => p + 1)}>Next</Button>
            </Flex>
          </Box>
        )}
      </Card>
    </Box>
  );
}
